import {
  ANDROID_DEFAULT_MASK_INPUT_OPTIONS,
  ANDROID_MASK_INPUT_OPTIONS,
  EMPTY_ANDROID_CONFIG,
  EMPTY_IOS_CONFIG,
  EMPTY_JS_CONFIG,
  IOS_DEFAULT_MASK_INPUT_OPTIONS,
  IOS_MASK_INPUT_OPTIONS,
  OTHER_MASKING_TYPES,
} from './constants';
import type { AndroidDraftConfig, CompressionQuality, IosDraftConfig, JsDraftConfig, SessionReplayApiData } from './types';

export const arraysEqual = (a: string[] = [], b: string[] = []) => {
  if (a.length !== b.length) return false;
  return a.every((val, idx) => val === b[idx]);
};

export const normalizeSelector = (s: string) => s.trim().toLowerCase();

export const clampPercentage = (v: number) => Math.max(0, Math.min(100, v));

export const normalizeCompressionQuality = (raw: unknown): CompressionQuality => {
  const v = typeof raw === 'string' ? raw.toLowerCase() : '';
  if (v === 'low' || v === 'medium' || v === 'high') return v;
  return 'low';
};

const extractStringValues = (raw: unknown): string[] => {
  // Handles nested arrays and object-ish shapes.
  const out: string[] = [];
  const stack: unknown[] = [raw];
  while (stack.length) {
    const cur = stack.pop();
    if (!cur) continue;
    if (typeof cur === 'string') {
      out.push(cur);
      continue;
    }
    if (Array.isArray(cur)) {
      for (const item of cur) stack.push(item);
      continue;
    }
    if (typeof cur === 'object') {
      const candidate =
        (cur as any).value ?? (cur as any).type ?? (cur as any).name ?? (cur as any).key ?? (cur as any).id;
      if (typeof candidate === 'string') out.push(candidate);
    }
  }
  return out;
};

export const normalizeMaskInputOptions = (raw: unknown): string[] => {
  // API can return:
  // - string[] (legacy)
  // - array of objects (newer), e.g. [{ value: 'search' }, ...]
  // - comma-separated string (edge)
  // We normalize to the known allowed list, in a stable display order.
  const values: string[] =
    typeof raw === 'string'
      ? raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : extractStringValues(raw).map((s) => s.trim()).filter(Boolean);

  const set = new Set(values);
  return OTHER_MASKING_TYPES.filter((t) => set.has(t));
};

export const normalizeApiData = (raw: any): SessionReplayApiData | undefined => {
  if (!raw) return raw;
  if (raw?.js || raw?.android || raw?.ios) return raw as SessionReplayApiData;
  // Back-compat: older API returned a single (web) config object.
  return {
    js: { exists: true, config: raw },
    metadata: (raw as any)?.metadata,
  };
};

export const normalizeJsConfig = (raw: any): JsDraftConfig => {
  if (!raw) return EMPTY_JS_CONFIG;
  const enabled = Boolean(raw.enabled);
  const samplingPct = clampPercentage(
    Number.isFinite(Number(raw.samplingPct ?? raw.samplingPercentage)) ? Number(raw.samplingPct ?? raw.samplingPercentage) : 10,
  );
  const blockSelector = Array.isArray(raw.blockSelector) ? raw.blockSelector : [];
  const ignoreSelector = Array.isArray(raw.ignoreSelector) ? raw.ignoreSelector : [];
  const maskTextSelector = Array.isArray(raw.maskTextSelector) ? raw.maskTextSelector : [];
  const maskAllInputs = Boolean(raw.maskAllInputs);
  const normalized = normalizeMaskInputOptions(raw.maskInputOptions);
  const maskInputOptions = maskAllInputs ? [...OTHER_MASKING_TYPES] : normalized;
  const mousemove = Boolean(raw.mousemove);
  return {
    enabled,
    samplingPct,
    blockSelector,
    ignoreSelector,
    maskTextSelector,
    maskAllInputs,
    maskInputOptions,
    mousemove,
  };
};

const normalizeMobileMaskInputOptions = (
  raw: unknown,
  allowed: string[],
  defaults: string[],
  maskAllInputs: boolean,
): string[] => {
  const values = extractStringValues(raw).map((s) => s.trim()).filter(Boolean);
  const set = new Set(values);
  const normalized = allowed.filter((t) => set.has(t));
  // Defaults are implicit (shown separately in UI), so keep this list strictly user-selected options.
  // If API provides nothing, this should stay empty.
  void defaults;
  return maskAllInputs ? [...allowed] : normalized;
};

export const normalizeAndroidConfig = (raw: any): AndroidDraftConfig => {
  if (!raw) return EMPTY_ANDROID_CONFIG;
  const enabled = Boolean(raw.enabled);
  const samplingPct = clampPercentage(Number.isFinite(Number(raw.samplingPct)) ? Number(raw.samplingPct) : 10);
  const maskAllInputs = Boolean(raw.maskAllInputs);
  const maskInputOptions = normalizeMobileMaskInputOptions(
    raw.maskInputOptions,
    ANDROID_MASK_INPUT_OPTIONS,
    ANDROID_DEFAULT_MASK_INPUT_OPTIONS,
    maskAllInputs,
  );
  const maskAllImages = Boolean(raw.maskAllImages);
  const compressionQuality = normalizeCompressionQuality(raw.compressionQuality);
  return { enabled, samplingPct, maskAllInputs, maskInputOptions, maskAllImages, compressionQuality };
};

export const normalizeIosConfig = (raw: any): IosDraftConfig => {
  if (!raw) return EMPTY_IOS_CONFIG;
  const enabled = Boolean(raw.enabled);
  const samplingPct = clampPercentage(Number.isFinite(Number(raw.samplingPct)) ? Number(raw.samplingPct) : 10);
  const maskAllInputs = Boolean(raw.maskAllInputs);
  const maskInputOptions = normalizeMobileMaskInputOptions(
    raw.maskInputOptions,
    IOS_MASK_INPUT_OPTIONS,
    IOS_DEFAULT_MASK_INPUT_OPTIONS,
    maskAllInputs,
  );
  const maskAllImages = Boolean(raw.maskAllImages);
  const maskSandboxedSystemViews = Boolean(raw.maskSandboxedSystemViews);
  const compressionQuality = normalizeCompressionQuality(raw.compressionQuality);
  return {
    enabled,
    samplingPct,
    maskAllInputs,
    maskInputOptions,
    maskAllImages,
    maskSandboxedSystemViews,
    compressionQuality,
  };
};
